import React, { useState, useEffect, useRef } from 'react';
import informationIcon from '../../Icons/information_icon.webp';
import toast from 'react-hot-toast';
import {
    BreadCrumbs,
    Select,
    OnOffSwitch,
    PrimaryButton,
    PageTitleWithToolTip,
    ExitConfirmationModal,
} from '../Common';
import { postRequestHeaders, trakrefFontFamily } from '../../utils';
import AssignedLocations from '../Account/AssignedLocations';
import Routes from '../../routes';
import { Alert, Box, Divider, Grid, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useNavigate from '../../hooks/useNavigate';
import UserInfo from '../../UserInfo';
import useFormDirty from '../../hooks/useFormDirty';
import { Grey1 } from '../../color_utils';

const commonContainerProps = { container: true, spacing: 2, width: "80%" };
const toastDuration = { duration: 6000 };

const LinkServiceProviders = ({
    history: {
        location: {
            state: {
                id,
                enabled,
                servicerInstanceId,
                canManageLocations,
                requireAdminApproval,
                restrictedLocations,
                preferredVendor,
                isEditing = false,
                assignedLocationIds = [],
                gettingServicerLocations = false,
                // This screen is used for both "Manage Service Providers" & "My Service Providers"
                viewingManageServiceProviders = true,
                allowedLocationList
            }
        } = {}
    } = {}
}) => {
    const linkServiceProvidersText = `${isEditing ? 'Edit' : 'Link'} Service Provider`;
    const [serviceProviderData, setServiceProviderData] = useState({
        id: null, // Id of the database record
        servicerInstanceId: null,
        canManageLocations: false,
        requireAdminApproval: false,
        restrictedLocations: false,
        preferredVendor: false,
    });
    const [instance, setInstance] = useState(null);
    const [options, setOptions] = useState({ serviceProviders: [], instance: [] });
    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [locationPermissionAccessToAll, setLocationPermissionAccessToAll] = useState(null);


    const requiredFields = ["servicerInstanceId"];
    const [showExitModal, setShowExitModal] = useState(false);
    const { initializeData, isDirty } = useFormDirty();
    const originalRestrictedLocations = useRef(null);

    const navigate = useNavigate({ pageName: 'LinkServiceProviders' });

    const setServiceProvider = data => setServiceProviderData(prev => ({ ...prev, ...data }));

    const validate = () => {
        if (requiredFields.length <= 0)
            return;

        const newErrors = {};

        requiredFields.forEach(field => {
            if ([null, undefined, '', 0].includes(serviceProviderData[field])) {
                newErrors[field] = true;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const submitData = async () => {
        console.log('serviceProviderData', serviceProviderData);
        const { restrictedLocations, assignedLocationIds } = serviceProviderData;

        if (!validate()) {
            return;
        }

        // User selected "Restricted Locations" but has not selected at least 1 location
        if (restrictedLocations && assignedLocationIds && assignedLocationIds.length <= 0) {
            return toast.error("Please select at least 1 Location!", toastDuration);
        }

        const response = await fetch(Routes.Instance.LinkServiceProvider(isEditing), {
            method: 'POST',
            headers: { ...postRequestHeaders },
            body: JSON.stringify(serviceProviderData)
        });

        if (response.status === 200) {
            toast.success(`The Service Provider has been successfully ${isEditing ? 'edited' : 'linked'}.`, toastDuration);
            return navigate.push("/ManageServiceProviders");
        }

        return toast.error(`Could not ${isEditing ? 'edit' : 'link'} service provider. Please try again.`, toastDuration);
    };

    const fetchData = async () => {
        const response = await fetch(Routes.Instance.GetServiceProviders(true, isEditing));
        const data = await response.json();
        setOptions(prev => ({
            ...prev,
            serviceProviders: data.servicers,
            instance: [data.instance]
        }));
        setInstance(data.instance.id);

        // User is editing an exisiting record
        if (id) {
            const newServiceProviderData = {
                id,
                enabled,
                servicerInstanceId,
                canManageLocations,
                requireAdminApproval,
                restrictedLocations,
                preferredVendor,
                assignedLocationIds: [...allowedLocationList].sort((a, b) => a - b)
            };
            setServiceProviderData(newServiceProviderData);
            initializeData(newServiceProviderData);
            originalRestrictedLocations.current = restrictedLocations;
        }

        setLoading(false);
    };

    const onHandleCloseForm = () => {
        if (isEditing && isDirty(serviceProviderData)) {
            setShowExitModal(true);
            return;
        }
        navigate.goBack("/ManageServiceProviders");
    };


    useEffect(() => { fetchData() }, []);

    const onRestrictedLocationsChanged = () => {
        if (instance === serviceProviderData.servicerInstanceId) {
            return toast(() => (
                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: "0px",
                    margin: "0px",
                    gap: "10px"
                }} >
                    <div>
                        <img src={informationIcon} width={"17px"} height={"17px"} />
                    </div>
                    <div>
                        <span>
                            Users for this account have access to all locations. <br />
                            To restrict locations for certain users, locate the Assigned Locations tab for the user under the <span style={{ textDecoration: 'underline', cursor: 'pointer' }} onClick={() => navigate.push("/Users")}>`Manage Users`</span> section.
                        </span>
                    </div>
                </div>
            ), { duration: 10000 });
        }

        setServiceProvider({ restrictedLocations: !serviceProviderData.restrictedLocations });
    };

    return (
        <>
            <BreadCrumbs
                path={[
                    {
                        text: `${viewingManageServiceProviders ? "Geek Command Center" : "Account Details"}`,
                        to: `${viewingManageServiceProviders ? "/GeekCommandCenter" : "/Account"}`
                    },
                    {
                        text: `${viewingManageServiceProviders ? "Manage" : "My"} Service Providers`,
                        to: `${viewingManageServiceProviders ? "/ManageServiceProviders" : "/MyServiceProviders"}`
                    },
                    { text: linkServiceProvidersText }
                ]}
            />

            <Grid {...commonContainerProps}>
                <Grid item>
                    <IconButton aria-label="close" onClick={onHandleCloseForm}>
                        <CloseIcon />
                    </IconButton>
                </Grid>
                <Grid item xs={6}>
                    <PageTitleWithToolTip
                        pageTitle={linkServiceProvidersText}
                    />
                </Grid>
                <Grid item sx={{ marginLeft: "auto" }}>
                    <PrimaryButton
                        buttonText={"Save"}
                        onClick={submitData}
                        disabled={loading}
                    />
                </Grid>
            </Grid>

            <Grid {...commonContainerProps} mt={4} pl={6} ml={0}>
                <Grid item xs={6}>
                    <Select
                        disabled
                        label='Account'
                        value={instance}
                        onChange={e => setInstance(e.target.value)}
                        options={options.instance}
                    />
                </Grid>
                <Grid item xs={6}>
                    <Select
                        label='Service Provider'
                        value={serviceProviderData.servicerInstanceId}
                        required
                        onChange={e => {
                            setServiceProvider({ servicerInstanceId: e.target.value });
                            if (instance === e.target.value) {
                                setServiceProvider({ restrictedLocations: false });
                            }
                        }}
                        error={!!errors['servicerInstanceId']}
                        options={options.serviceProviders}
                        disabled={loading || isEditing}
                    />
                </Grid>
                {viewingManageServiceProviders && (
                    <>
                        <Grid item xs={6} sx={{ marginTop: '1%' }}>
                            <OnOffSwitch
                                label='Can Manage Locations'
                                value={serviceProviderData.canManageLocations}
                                onChange={() => setServiceProvider({ canManageLocations: !serviceProviderData.canManageLocations })}
                                disabled={loading}
                            />
                            <Divider />
                        </Grid>
                        <Grid item xs={6} sx={{ marginTop: '1%' }}>
                            <OnOffSwitch
                                label='Require Admin Approval'
                                value={serviceProviderData.requireAdminApproval}
                                onChange={() => setServiceProvider({ requireAdminApproval: !serviceProviderData.requireAdminApproval })}
                                disabled={loading}
                            />
                            <Divider />
                        </Grid>
                        <Grid item xs={6}>
                            <OnOffSwitch
                                label='Restricted Locations'
                                value={serviceProviderData.restrictedLocations}
                                onChange={onRestrictedLocationsChanged}
                                disabled={loading}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <OnOffSwitch
                                label='Preferred Service Provider'
                                value={serviceProviderData.preferredVendor}
                                onChange={() => setServiceProvider({ preferredVendor: !serviceProviderData.preferredVendor })}
                                disabled={loading}
                            />
                        </Grid>
                        {isEditing
                            && originalRestrictedLocations.current === true
                            && serviceProviderData.restrictedLocations === false
                            && (() => {
                                const providerName = options.serviceProviders
                                    .find(sp => sp.id === serviceProviderData.servicerInstanceId)?.name;
                                return (
                                    <Grid item xs={12}>
                                        <Alert severity="warning">
                                            {`Warning: Turning restrict locations off for ${providerName} will update all user level permissions for ${providerName} to Access to All Locations. Click Save to save these changes.`}
                                        </Alert>
                                    </Grid>
                                );
                            })()}
                    </>
                )}
            </Grid>

            <Grid container item mt={6} pl={6} ml={0}>
                <Stack sx={{ width: "100%" }}>
                    <Typography
                        sx={{
                            color: Grey1,
                            fontFamily: trakrefFontFamily,
                            fontSize: "18px",
                            fontStyle: "normal",
                            fontWeight: 400,
                            lineHeight: "normal",
                            ml: "30px"
                        }}
                    >
                        Assigned Locations
                    </Typography>
                    <div>
                        <AssignedLocations
                            linkServiceProviders
                            gettingServicerLocations={gettingServicerLocations}
                            userInformation={{
                                id: UserInfo.userId,
                                restrictedLocations: serviceProviderData.restrictedLocations
                            }}
                            navigate={navigate}
                            isOptionDisabled={!serviceProviderData.restrictedLocations}
                            servicerInstanceId={servicerInstanceId}
                            setLocationPermissionAccessToAll={setLocationPermissionAccessToAll}
                            setFormState={setServiceProviderData}
                        />
                    </div>

                </Stack>
            </Grid>
            <ExitConfirmationModal
                open={showExitModal}
                close={() => setShowExitModal(false)}
                leaveOnClick={() => navigate.goBack("/ManageServiceProviders")}
                stayOnClick={() => setShowExitModal(false)}
                isEditing={isEditing}
            />
        </>
    );
};

export default LinkServiceProviders;
